import { useState, useEffect, useCallback } from "react";
import { ShieldCheck, UserPlus, Trash2, Lock, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function AdminManager() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await api.get("/auth/admins");
      setAdmins(res.data.admins || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load admin list");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const addAdmin = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/auth/admins", { email });
      toast.success(`${email} now has full admin access`);
      setNewEmail("");
      await fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add admin");
    }
    setBusy(false);
  };

  const removeAdmin = async (email) => {
    if (!window.confirm(`Remove admin access for ${email}? Their active sessions will be signed out immediately.`)) return;
    setBusy(true);
    try {
      await api.delete(`/auth/admins/${encodeURIComponent(email)}`);
      toast.success(`${email} removed from admins`);
      await fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove admin");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 p-6" data-testid="admin-manager-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-8 h-8 text-amber-400" />
        <div>
          <h1 className="text-2xl font-bold">Admin Manager</h1>
          <p className="text-sm text-muted-foreground">
            Everyone below has full admin abilities. Owner accounts are locked and can only be changed by the developer.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5 space-y-3">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-emerald-400" /> Add an admin
        </h2>
        <div className="flex gap-3">
          <Input
            data-testid="admin-add-email-input"
            type="email"
            placeholder="name@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addAdmin()}
            className="flex-1"
          />
          <Button
            data-testid="admin-add-submit-button"
            onClick={addAdmin}
            disabled={busy || !newEmail.trim()}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Admin"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          They sign in with Google using this email and immediately get full access.
        </p>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900/60 divide-y divide-slate-800" data-testid="admin-list">
        {loading ? (
          <div className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading admins…
          </div>
        ) : admins.map((a) => (
          <div key={a.email} className="flex items-center justify-between px-5 py-4" data-testid={`admin-row-${a.email}`}>
            <div>
              <div className="font-medium flex items-center gap-2">
                {a.email}
                {a.email.toLowerCase() === (user?.email || "").toLowerCase() && (
                  <span className="text-xs rounded-full bg-slate-700 px-2 py-0.5 text-slate-300">you</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {a.protected
                  ? "Owner account"
                  : `Added by ${a.added_by || "—"}${a.added_at ? ` · ${new Date(a.added_at).toLocaleDateString()}` : ""}`}
              </div>
            </div>
            {a.protected ? (
              <span className="flex items-center gap-1 text-xs text-amber-400" data-testid={`admin-protected-${a.email}`}>
                <Lock className="w-3.5 h-3.5" /> Protected
              </span>
            ) : (
              <Button
                data-testid={`admin-remove-button-${a.email}`}
                variant="ghost"
                size="sm"
                className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                disabled={busy || a.email.toLowerCase() === (user?.email || "").toLowerCase()}
                onClick={() => removeAdmin(a.email)}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
